import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import css from './SemiFinishedAddProductModal.module.css';
import { productsActions } from "../../../../store/slices/settings-slices";
import { semiFinishedProductsActions } from "../../../../store/slices/semiFinished-slice";
import { SearchableSelect } from "../../../../ui";

const SemiFinishedAddProductModal = ({ setSemiFinishedProductAddOpen }) => {
    const [selectedProducts, setSelectedProducts] = useState("");
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const { productsArray, trigger } = useSelector(store => store.products);
    const { semiFinishedProductsForModalArray } = useSelector(store => store.semiFinishedProducts);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm();

    useEffect(() => {
        dispatch(productsActions.getAllProducts());
    }, [trigger]);

    const handleAddProductSubmit = (data) => {
        const selectedProduct = productsArray.find(obj => obj.id === selectedProducts);
        if (!selectedProduct) return;

        const weight = Number(data.weight);
        let weightNetto;

        // Check if user manually entered weightNetto, otherwise auto-calculate
        if (data.weightNetto && Number(data.weightNetto) > 0) {
            weightNetto = Number(data.weightNetto);
        } else {
            // Calculate automatic weightNetto based on the product's own ratio
            const originalWeight = parseFloat(selectedProduct.weight) || 0;
            const originalWeightNetto = parseFloat(selectedProduct.weightNetto) || 0;
            const nettoRatio = originalWeight > 0 ? originalWeightNetto / originalWeight : 0;
            weightNetto = Number((weight * nettoRatio).toFixed(2));
        }

        const singleProduct = {
            id: selectedProduct.id,
            name: selectedProduct.name,
            weight,
            weightNetto,
            cost: Number(((selectedProduct.price / 1000) * weight).toFixed(2)),
            product: {
                // Products store nutrients per 100g raw, calculate based on raw weight added
                // NOT cooked weight - the nutrients from raw ingredients don't disappear
                energy: Number(((selectedProduct.energy * weight) / 100).toFixed(2)),
                fats: Number(((selectedProduct.fats * weight) / 100).toFixed(2)),
                proteins: Number(((selectedProduct.proteins * weight) / 100).toFixed(2)),
                carbohydrates: Number(((selectedProduct.carbohydrates * weight) / 100).toFixed(2)),
            }
        };

        dispatch(semiFinishedProductsActions.addDishProductsForModal(singleProduct));
        dispatch(semiFinishedProductsActions.changeTrigger());
        setSemiFinishedProductAddOpen(false);
    };

    // Prepare options for SearchableSelect
    const productOptions = productsArray.map(product => ({
        id: product.id,
        name: product.name,
        label: product.name[currentLanguage] || product.name['en']
    }));

    return (
        <section className={css.overlay}>
            <div className={css.modal}>
                <button className={css.closeButton} onClick={() => setSemiFinishedProductAddOpen(false)}>
                    &times;
                </button>
                <h2 className={css.title}>Add product</h2>

                <form className={css.content} onSubmit={handleSubmit(handleAddProductSubmit)}>
                    <div className={css.inputGroup}>
                        <label htmlFor="group">Products</label>
                        <SearchableSelect
                            options={productOptions}
                            value={selectedProducts}
                            onChange={setSelectedProducts}
                            placeholder={t('settings-menu-title-products')}
                            searchPlaceholder="Type to search products..."
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => 
                                semiFinishedProductsForModalArray.some(p => 
                                    p.id === option.id || p.productId === option.id
                                )
                            }
                            className={css.searchableSelect}
                        />
                    </div>

                    <div className={css.inputGroup}>
                        <label htmlFor="weight">{t('Weight')}</label>
                        <input
                            type="number"
                            id="weight"
                            {...register("weight", { required: true })}
                            className={errors.weight ? css.inputError : ""}
                        />
                        {errors.weight && <p className={css.error}>{errors.weight.message}</p>}
                    </div>

                    <div className={css.inputGroup}>
                        <label htmlFor="weightNetto">{t('WeightNetto')}</label>
                        <input
                            type="number"
                            id="weightNetto"
                            {...register("weightNetto")}
                            className={errors.weightNetto ? css.inputError : ""}
                        />
                        {errors.weightNetto && <p className={css.error}>{errors.weightNetto.message}</p>}
                    </div>

                    <div className={css.actions}>
                        <button type="button" className={css.cancelButton} onClick={() => setSemiFinishedProductAddOpen(false)}>
                            Cancel
                        </button>
                        <button type="submit" className={css.okButton}>OK</button>
                    </div>
                </form>
            </div>
        </section>
    );
};

export { SemiFinishedAddProductModal };
